// landing-pages.js — Two stand-alone "landing" screens that bypass
// the normal dashboard chrome:
//
//   /device?code=<user_code>     — terminal device-authorization flow.
//                                  `agc login` opens this URL in the
//                                  user's browser; they sign in and
//                                  approve the displayed code, which
//                                  unblocks the CLI.
//
//   /accept?token=<invite_token> — invitation accept landing. New
//                                  hire pastes their password, the
//                                  coordinator creates/verifies the
//                                  account, and we auto-sign-in and
//                                  poll for daemon check-in.
//
// Both are one-shot pages: they fully repaint the `#device-screen` /
// `#accept-screen` element, replacing the dashboard chrome entirely.
// Called once each from bootstrap.js's boot() router.
//
// Both use COORDINATOR + Supabase. Supabase is bridged because the
// `let` binding lives in bootstrap.js's closure.
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2o).
import { escapeHTML } from "./util.js";

const COORDINATOR =
  (typeof window !== "undefined" &&
    window.AC_CONFIG &&
    window.AC_CONFIG.API_BASE) ||
  "";

function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

function hideLoading() {
  const el = document.getElementById("loading-screen");
  if (el) el.style.display = "none";
}

// ---- /device route handler ----
//
// Part of the `agc login` device-authorization flow.
//
// Flow:
//   1. Daemon calls POST /v1/auth/device/start, gets a user_code
//      (XXXX-XXXX) and opens dashboard.agentcollision.com/device?
//      code=XXXX-XXXX in the user's browser.
//   2. This page renders. If the user isn't signed in, we stash a
//      return-to in sessionStorage so they come back here after login.
//   3. Once signed in, we GET /v1/auth/device/:user_code to render
//      details (the machine label, expiration).
//   4. User clicks Approve → POST /v1/auth/device/:user_code with
//      their Supabase JWT, daemon picks up the approval, redirects
//      to the dashboard.

export async function renderDeviceApprovalPage() {
  hideLoading();
  const screen = document.getElementById("device-screen");
  const body = document.getElementById("device-body");
  screen.style.display = "";

  const urlCode = (new URLSearchParams(location.search).get("code") || "")
    .toUpperCase()
    .trim();

  // Require a Supabase session — the whole point of this page is to let an
  // authenticated user delegate that authority to a daemon.
  //
  // We reach this page via location.replace('/device') right after sign-in (a
  // full reload), so the Supabase client must re-hydrate the session from
  // localStorage. That hydration is ASYNC: the first getSession() can return
  // null even though the user just authenticated. Combined with the SIGNED_IN
  // return-to bounce, a single null read here re-shows "Sign in" → loop. So we
  // (1) wait for the AC_LEGACY bridge to expose the client, then (2) poll
  // getSession() a few times until it returns the session that's being restored.
  let supabase = getSupabase();
  if (!supabase) {
    for (let i = 0; i < 30 && !supabase; i++) {
      await new Promise((r) => setTimeout(r, 100));
      supabase = getSupabase();
    }
  }
  let session = null;
  if (supabase) {
    for (let i = 0; i < 10; i++) {
      try {
        const res = await supabase.auth.getSession();
        session = res && res.data && res.data.session;
      } catch (_) {}
      if (session) break;
      await new Promise((r) => setTimeout(r, 150));
    }
  }
  if (!session) {
    // Store the intended destination so after login we come back here.
    const returnTo =
      "/device" + (urlCode ? "?code=" + encodeURIComponent(urlCode) : "");
    try {
      sessionStorage.setItem("ac-device-return-to", returnTo);
    } catch {}
    body.innerHTML = `
      <h1 style="font-size: 22px; font-weight: 700; margin-bottom: 12px;">Sign in to continue</h1>
      <p style="color: var(--text-secondary); font-size: 14px; margin-bottom: 20px; line-height: 1.6;">
        You need to sign in to approve this device. We'll send you back here right after.
      </p>
      <a href="/" class="btn btn-primary" style="display: inline-block; padding: 10px 24px;">Sign in</a>
    `;
    return;
  }

  // No code in URL — ask the user to paste one (some users copy just
  // the code from the terminal and open dashboard.agentcollision.com/
  // device without the query string).
  if (!urlCode) {
    body.innerHTML = `
      <h1 style="font-size: 22px; font-weight: 700; margin-bottom: 12px;">Paste your login code</h1>
      <p style="color: var(--text-secondary); font-size: 14px; margin-bottom: 20px; line-height: 1.6;">
        Your terminal shows a code like <code>ABCD-1234</code>. Paste it here.
      </p>
      <input type="text" id="device-manual-code" placeholder="ABCD-1234" autocomplete="off"
             style="width: 100%; padding: 12px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 10px; color: var(--text); font-size: 16px; text-align: center; font-family: monospace; letter-spacing: 0.1em; margin-bottom: 16px; text-transform: uppercase;" />
      <button id="device-manual-go" class="btn btn-primary" style="width: 100%; padding: 12px;">Continue</button>
    `;
    document.getElementById("device-manual-go").addEventListener("click", () => {
      const v = document.getElementById("device-manual-code").value.trim().toUpperCase();
      if (v) location.search = "?code=" + encodeURIComponent(v);
    });
    document.getElementById("device-manual-code").addEventListener("keydown", (e) => {
      if (e.key === "Enter") document.getElementById("device-manual-go").click();
    });
    return;
  }

  // Look up the pending request so we can show the user what they're
  // approving.
  let lookup;
  try {
    const r = await fetch(COORDINATOR + "/v1/auth/device/" + encodeURIComponent(urlCode));
    lookup = await r.json();
    if (!r.ok) {
      const msg = lookup.message || "Code not found or expired.";
      body.innerHTML = `
        <h1 style="font-size: 22px; font-weight: 700; margin-bottom: 12px; color: var(--danger);">Login code expired</h1>
        <p style="color: var(--text-secondary); font-size: 14px; margin-bottom: 20px; line-height: 1.6;">${escapeHTML(msg)}</p>
        <p style="color: var(--text-tertiary); font-size: 13px; margin-bottom: 20px;">Codes are valid for 10 minutes. Run <code>agc login</code> again to start over.</p>
        <a href="/" class="btn btn-sm">Go to dashboard</a>
      `;
      return;
    }
  } catch (e) {
    body.innerHTML = '<p style="color: var(--danger);">Failed to look up login code.</p>';
    return;
  }

  if (lookup.status !== "pending") {
    // Already approved, denied, or consumed. Tell the user plainly.
    const statusMsg =
      lookup.status === "approved" || lookup.status === "consumed"
        ? "This code has already been approved. Your terminal should now be logged in."
        : "This code is no longer active (status: " +
          lookup.status +
          "). Run <code>agc login</code> again if you need a new one.";
    body.innerHTML = `
      <h1 style="font-size: 22px; font-weight: 700; margin-bottom: 12px;">Already handled</h1>
      <p style="color: var(--text-secondary); font-size: 14px; margin-bottom: 20px; line-height: 1.6;">${statusMsg}</p>
      <a href="/" class="btn btn-sm">Go to dashboard</a>
    `;
    return;
  }

  // Pending — show the approve UI.
  const label = lookup.machine_label ? escapeHTML(lookup.machine_label) : "this machine";
  body.innerHTML = `
    <h1 style="font-size: 22px; font-weight: 700; margin-bottom: 12px;">Approve this device?</h1>
    <p style="color: var(--text-secondary); font-size: 14px; margin-bottom: 24px; line-height: 1.6;">
      AgentCollision on <strong>${label}</strong> wants to connect to your account.
    </p>
    <div style="background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 10px; padding: 16px; margin-bottom: 24px; font-family: monospace; font-size: 18px; letter-spacing: 0.1em; color: var(--accent);">
      ${escapeHTML(urlCode)}
    </div>
    <p style="color: var(--text-tertiary); font-size: 12px; margin-bottom: 24px;">
      Make sure this code matches what your terminal is showing. If it doesn't, cancel and start over.
    </p>
    <button id="device-approve-btn" class="btn btn-primary" style="width: 100%; padding: 12px; margin-bottom: 12px;">Approve</button>
    <a href="/" class="btn btn-sm" style="display: inline-block;">Cancel</a>
    <div id="device-approve-status" style="margin-top: 16px; font-size: 13px; min-height: 18px;"></div>
  `;

  document.getElementById("device-approve-btn").addEventListener("click", async () => {
    const btn = document.getElementById("device-approve-btn");
    const statusEl = document.getElementById("device-approve-status");
    btn.disabled = true;
    btn.textContent = "Approving...";
    statusEl.textContent = "";
    try {
      const {
        data: { session: s },
      } = await supabase.auth.getSession();
      const r = await fetch(COORDINATOR + "/v1/auth/device/" + encodeURIComponent(urlCode), {
        method: "POST",
        headers: {
          Authorization: "Bearer " + s.access_token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await r.json();
      if (!r.ok) {
        statusEl.innerHTML =
          '<span style="color: var(--danger);">' +
          escapeHTML(data.message || "Approval failed (" + r.status + ")") +
          "</span>";
        btn.disabled = false;
        btn.textContent = "Approve";
        return;
      }
      // Success — show a brief confirmation, then auto-redirect to the
      // dashboard. Terminal finishes in the background; the user wants
      // to be in their dashboard, not staring at a "you can close this
      // tab" page.
      body.innerHTML = `
        <div style="font-size: 48px; margin-bottom: 16px;">&#10003;</div>
        <h1 style="font-size: 22px; font-weight: 700; margin-bottom: 12px;">Approved</h1>
        <p style="color: var(--text-secondary); font-size: 14px; margin-bottom: 24px; line-height: 1.6;">
          This machine is now connected. Redirecting you to the dashboard&hellip;
        </p>
        <a href="/" class="btn btn-sm">Go now</a>
      `;
      setTimeout(() => {
        window.location.replace("/");
      }, 1500);
    } catch (e) {
      statusEl.innerHTML =
        '<span style="color: var(--danger);">' +
        escapeHTML((e && e.message) || "Network error") +
        "</span>";
      btn.disabled = false;
      btn.textContent = "Approve";
    }
  });
}

// ---- /accept route handler ----
//
// Invitation-accept landing. The user clicks a link from email
// (/accept?token=<invite_token>); we look up the invitation, render
// a password form, POST it to /v1/invitations/<tok>/accept, then
// auto-sign-in via Supabase + poll for daemon check-in.

export async function renderAcceptInvitationPage(acceptTok) {
  hideLoading();
  const body = document.getElementById("accept-body");
  document.getElementById("accept-screen").style.display = "";

  // Fetch invitation details
  let inv;
  try {
    const r = await fetch(COORDINATOR + "/v1/invitations/" + encodeURIComponent(acceptTok));
    const data = await r.json();
    if (!r.ok) {
      body.innerHTML =
        '<h1 style="font-size: 20px; font-weight: 700; margin-bottom: 12px; color: var(--danger);">Invitation not available</h1>' +
        '<p style="color: var(--text-secondary); font-size: 14px; line-height: 1.6; margin-bottom: 20px;">' +
        escapeHTML(data.message || "This invitation cannot be used.") +
        "</p>" +
        '<a href="/" class="btn btn-sm">Back to dashboard</a>';
      return;
    }
    inv = data;
  } catch (e) {
    body.innerHTML = '<p style="color: var(--danger);">Failed to load invitation.</p>';
    return;
  }

  // Render the accept form. The user enters their password (new or
  // existing), and the backend creates or verifies their account via
  // the admin API. The invite token itself authenticates the request
  // — no Supabase signUp round-trip.
  body.innerHTML = `
    <h1 style="font-size: 22px; font-weight: 700; margin-bottom: 8px;">You're invited to ${escapeHTML(inv.team_name)}</h1>
    <p style="color: var(--text-secondary); font-size: 14px; margin-bottom: 20px; line-height: 1.6;">
      ${escapeHTML(inv.invited_by || "An admin")} invited you to join as a <strong>${escapeHTML(inv.role)}</strong>.
    </p>
    <p style="font-size: 13px; color: var(--text-tertiary); margin-bottom: 16px;">Joining as <strong style="color: var(--text);">${escapeHTML(inv.email)}</strong></p>
    <input type="password" id="accept-password" placeholder="Password (min 8 chars)" autocomplete="new-password" style="width: 100%; padding: 10px 12px; margin-bottom: 10px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; color: var(--text); font-size: 14px; box-sizing: border-box;" />
    <button class="btn btn-primary" id="accept-submit-btn" style="width: 100%;">Accept invitation</button>
    <p style="font-size: 11px; color: var(--text-tertiary); margin-top: 12px; line-height: 1.5;">New account? Choose a password above.<br/>Already have one? Use your existing password &mdash; we'll sign you in.</p>
    <div id="accept-error" style="color: var(--danger); font-size: 13px; margin-top: 10px;"></div>`;

  document.getElementById("accept-password").addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") document.getElementById("accept-submit-btn").click();
  });

  document.getElementById("accept-submit-btn").onclick = async () => {
    const pw = document.getElementById("accept-password").value;
    const err = document.getElementById("accept-error");
    const btn = document.getElementById("accept-submit-btn");
    if (!pw || pw.length < 8) {
      err.textContent = "Password must be at least 8 characters.";
      return;
    }
    err.textContent = "";
    btn.disabled = true;
    btn.textContent = "Joining...";
    try {
      const r = await fetch(
        COORDINATOR + "/v1/invitations/" + encodeURIComponent(acceptTok) + "/accept",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: pw }),
        },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        err.textContent = data.message || "Accept failed (HTTP " + r.status + ")";
        btn.disabled = false;
        btn.textContent = "Accept invitation";
        return;
      }
      // Sign the user in via Supabase with the password they just
      // provided. For a newly created account this is the first
      // sign-in; for existing users it's a normal sign-in. If this
      // fails (e.g. wrong password on an existing account) the user
      // still gets the success page and can sign in manually — log
      // so we can diagnose if it becomes a pattern.
      try {
        const supabase = getSupabase();
        if (supabase) {
          await supabase.auth.signInWithPassword({
            email: data.email || inv.email,
            password: pw,
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[accept-invitation] auto-signin failed, user must sign in manually:", e);
      }

      // Render the success state with install command + live status.
      body.innerHTML = `
        <h1 style="font-size: 22px; font-weight: 700; margin-bottom: 8px; color: var(--success);">&check; You're in</h1>
        <p style="color: var(--text-secondary); font-size: 14px; margin-bottom: 20px; line-height: 1.6;">
          Welcome to ${escapeHTML(inv.team_name)}! To finish setup, install AgentCollision on your computer:
        </p>
        <div style="background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; padding: 12px; margin-bottom: 16px;">
          <code style="font-size: 12px; font-family: var(--font-mono); color: var(--accent); word-break: break-all;" id="accept-install-cmd">${escapeHTML(data.install_command || "")}</code>
        </div>
        <div style="display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px;">
          <button class="btn btn-sm" id="accept-copy-cmd">Copy command</button>
          <a href="/" class="btn btn-sm btn-primary">Open dashboard</a>
        </div>

        <!-- Live daemon status -->
        <div id="accept-daemon-status" style="background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 8px; padding: 14px 16px; margin-bottom: 16px; display: flex; align-items: center; gap: 12px;">
          <div id="accept-daemon-spinner" style="width: 14px; height: 14px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: accept-spin 0.8s linear infinite; flex-shrink: 0;"></div>
          <div style="flex: 1;">
            <div style="font-size: 13px; font-weight: 600; color: var(--text);" id="accept-daemon-title">Waiting for your daemon&hellip;</div>
            <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;" id="accept-daemon-detail">Run the install command above. This will turn green the moment your machine checks in.</div>
          </div>
        </div>
        <style>@keyframes accept-spin{to{transform:rotate(360deg)}}</style>

        <p style="font-size: 11px; color: var(--text-tertiary);">Don't have Homebrew? See the <a href="https://docs.agentcollision.com/quickstart.html" style="color: var(--accent);">quickstart guide</a>.</p>`;

      document.getElementById("accept-copy-cmd").onclick = async () => {
        const cmd = document.getElementById("accept-install-cmd").textContent;
        try {
          await navigator.clipboard.writeText(cmd);
          document.getElementById("accept-copy-cmd").textContent = "Copied!";
        } catch {}
      };

      // Poll the coordinator for the install token's check-in status.
      // Once the daemon reports in, flip the card to green and stop
      // polling.
      const installToken = data.install_token;
      if (installToken) {
        let pollCount = 0;
        const MAX_POLLS = 120; // ~10 min of 5s polls
        const poll = async () => {
          pollCount++;
          try {
            const r2 = await fetch(
              COORDINATOR +
                "/v1/install-tokens/" +
                encodeURIComponent(installToken) +
                "/status",
            );
            const s = await r2.json();
            if (s.connected) {
              const status = document.getElementById("accept-daemon-status");
              const spinner = document.getElementById("accept-daemon-spinner");
              const title = document.getElementById("accept-daemon-title");
              const detail = document.getElementById("accept-daemon-detail");
              if (status) status.style.borderColor = "var(--success)";
              if (status) status.style.background = "rgba(52,211,153,0.08)";
              if (spinner) {
                spinner.style.animation = "none";
                spinner.style.border = "none";
                spinner.innerHTML =
                  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
              }
              if (title)
                title.innerHTML =
                  'Daemon connected <span style="color: var(--success);">•</span> ' +
                  escapeHTML(s.laptop_id || "machine");
              if (detail)
                detail.textContent =
                  "You’re ready to go. Open the dashboard to see your team’s coordination in real time.";
              return; // stop polling
            }
          } catch {}
          if (pollCount >= MAX_POLLS) {
            const detail = document.getElementById("accept-daemon-detail");
            if (detail)
              detail.innerHTML =
                'Haven’t seen your daemon yet. <a href="https://docs.agentcollision.com/troubleshooting.html" style="color: var(--accent);">Need help?</a>';
            return;
          }
          setTimeout(poll, 5000);
        };
        setTimeout(poll, 3000); // give the user a second to run the command
      }
    } catch (e) {
      console.error("[accept-invitation] exception:", e);
      err.textContent = e && e.message ? e.message : "Accept failed";
      btn.disabled = false;
      btn.textContent = "Accept invitation";
    }
  };
}
